import React, { useEffect, useState, useMemo } from "react";
import { api, extractError } from "@/lib/api";
import { Link } from "react-router-dom";
import { formatDateIt } from "@/lib/format";
import { Plus, X, ChevronDown, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import ClientAutocomplete from "@/components/ClientAutocomplete";
import NewClientModal from "@/components/NewClientModal";

const SORT_KEYS = [
    { key: "title", label: "Titolo" },
    { key: "type", label: "Tipo" },
    { key: "client", label: "Cliente" },
    { key: "status", label: "Stato" },
    { key: "deadline", label: "Scadenza" },
];

function classifyStatus(status) {
    if (!status) return "active";
    if (status.is_final) return "concluso";
    if ((status.name || "").toLowerCase().includes("pausa")) return "pausa";
    return "active";
}

export default function Projects() {
    const [list, setList] = useState([]);
    const [clients, setClients] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [users, setUsers] = useState([]);
    const [fType, setFType] = useState("");
    const [fClient, setFClient] = useState("");
    const [fStatus, setFStatus] = useState("");
    const [fResponsible, setFResponsible] = useState("");
    const [sortKey, setSortKey] = useState("title");
    const [sortDir, setSortDir] = useState("asc");
    const [expandPausa, setExpandPausa] = useState(false);
    const [expandConcluso, setExpandConcluso] = useState(false);
    const [show, setShow] = useState(false);
    const [feedbackFor, setFeedbackFor] = useState(null);
    const [metricsByProject, setMetricsByProject] = useState({});

    const load = async () => {
        const params = {};
        if (fType) params.project_type = fType;
        if (fClient) params.client_id = fClient;
        if (fStatus) params.status_id = fStatus;
        if (fResponsible) params.assignee_id = fResponsible;
        const { data } = await api.get("/projects", { params });
        const filtered = data.filter((p) => p.project_type !== "internal_long" && p.project_type !== "internal_weekly");
        setList(filtered);
        // Load per-project metrics for the progress bar
        const metrics = {};
        await Promise.all(filtered.map(async (p) => {
            try { metrics[p.id] = (await api.get(`/projects/${p.id}/metrics`)).data; }
            catch (e) { console.error(`Load metrics for ${p.id} failed:`, e); }
        }));
        setMetricsByProject(metrics);
    };
    useEffect(() => {
        (async () => {
            const [c, s, u] = await Promise.all([
                api.get("/clients"), api.get("/settings/project-statuses"), api.get("/users"),
            ]);
            setClients(c.data); setStatuses(s.data); setUsers(u.data);
        })();
    }, []);
    useEffect(() => { load(); }, [fType, fClient, fStatus, fResponsible]); // eslint-disable-line

    const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
    const statusMap = useMemo(() => Object.fromEntries(statuses.map((s) => [s.id, s])), [statuses]);

    const sorter = (a, b) => {
        const dir = sortDir === "asc" ? 1 : -1;
        const va = getSortValue(a, sortKey, clientMap, statusMap);
        const vb = getSortValue(b, sortKey, clientMap, statusMap);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        return va > vb ? dir : va < vb ? -dir : 0;
    };

    const grouped = useMemo(() => {
        const g = { active: [], pausa: [], concluso: [] };
        list.forEach((p) => {
            const cls = classifyStatus(statusMap[p.status_id]);
            g[cls].push(p);
        });
        g.active.sort(sorter);
        g.pausa.sort(sorter);
        g.concluso.sort(sorter);
        return g;
    }, [list, statusMap, sortKey, sortDir]);

    const changeStatus = async (projectId, statusId) => {
        const prev = list.find((p) => p.id === projectId);
        const prevStatus = statusMap[prev?.status_id];
        const nextStatus = statusMap[statusId];
        // Optimistic UI: update local list immediately
        setList((prevList) => prevList.map((p) => (p.id === projectId ? { ...p, status_id: statusId } : p)));
        try {
            await api.patch(`/projects/${projectId}`, { status_id: statusId });
            // If just moved to "Concluso" -> offer feedback email
            if (nextStatus && (nextStatus.name || "").toLowerCase() === "concluso"
                && (prevStatus?.name || "").toLowerCase() !== "concluso") {
                setFeedbackFor(projectId);
            }
        } catch (e) {
            // Rollback on error
            load();
        }
    };

    return (
        <div className="p-6" data-testid="projects-page">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Progetti</h1>
                    <div className="text-sm text-neutral-500">{list.length} progetti</div>
                </div>
                <button onClick={() => setShow(true)} data-testid="new-project-btn"
                    className="inline-flex items-center gap-1.5 bg-neutral-900 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-neutral-800">
                    <Plus size={14} /> Nuovo progetto
                </button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-3 flex flex-wrap gap-2 mb-4 items-center">
                <select value={fType} onChange={(e) => setFType(e.target.value)} className="text-sm border border-neutral-200 rounded px-2 py-1.5" data-testid="filter-type">
                    <option value="">Tutti i tipi</option>
                    <option value="spot">Spot</option>
                    <option value="continuous">Continuativo</option>
                </select>
                <select value={fClient} onChange={(e) => setFClient(e.target.value)} className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutti i clienti</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
                <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutti gli stati</option>
                    {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select value={fResponsible} onChange={(e) => setFResponsible(e.target.value)} data-testid="filter-responsible"
                    className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutti i responsabili</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
                <div className="ml-auto flex items-center gap-2 text-xs text-neutral-500">
                    <span>Ordina per:</span>
                    <select data-testid="sort-key" value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                        {SORT_KEYS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                    <button onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")} data-testid="sort-dir"
                        className="text-sm border border-neutral-200 rounded px-2 py-1 inline-flex items-center gap-1 text-neutral-700 hover:bg-neutral-50">
                        {sortDir === "asc" ? <><ArrowUp size={13} /> Cresc.</> : <><ArrowDown size={13} /> Decr.</>}
                    </button>
                </div>
            </div>

            {list.length === 0 ? (
                <div className="bg-white border border-neutral-200 rounded-md p-10 text-center" data-testid="empty-state">
                    <div className="font-display text-neutral-800 font-medium">Nessun progetto</div>
                    <div className="text-sm text-neutral-500 mt-1">Crea il primo progetto.</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {/* Section: In corso / active */}
                    <ProjectsTable
                        projects={grouped.active}
                        clientMap={clientMap} statusMap={statusMap} statuses={statuses}
                        metricsByProject={metricsByProject}
                        onStatusChange={changeStatus}
                        testId="projects-active"
                    />

                    {/* Accordion In pausa (muted) */}
                    <Accordion
                        title="In pausa"
                        count={grouped.pausa.length}
                        expanded={expandPausa}
                        onToggle={() => setExpandPausa(!expandPausa)}
                        testId="folder-pausa"
                        variant="muted"
                    >
                        <ProjectsTable
                            projects={grouped.pausa}
                            clientMap={clientMap} statusMap={statusMap} statuses={statuses}
                            metricsByProject={metricsByProject}
                            onStatusChange={changeStatus}
                            testId="projects-pausa"
                            muted
                        />
                    </Accordion>

                    {/* Accordion Concluso (muted) */}
                    <Accordion
                        title="Concluso"
                        count={grouped.concluso.length}
                        expanded={expandConcluso}
                        onToggle={() => setExpandConcluso(!expandConcluso)}
                        testId="folder-concluso"
                        variant="muted"
                    >
                        <ProjectsTable
                            projects={grouped.concluso}
                            clientMap={clientMap} statusMap={statusMap} statuses={statuses}
                            metricsByProject={metricsByProject}
                            onStatusChange={changeStatus}
                            testId="projects-concluso"
                            muted
                        />
                    </Accordion>
                </div>
            )}

            {show && (
                <NewProjectModal clients={clients} statuses={statuses} users={users}
                    onClose={() => setShow(false)}
                    onCreated={() => { setShow(false); load(); }} />
            )}

            {feedbackFor && (
                <ProjectsFeedbackModal projectId={feedbackFor} onClose={() => setFeedbackFor(null)} />
            )}
        </div>
    );
}

function ProjectsFeedbackModal({ projectId, onClose }) {
    const [state, setState] = useState({ loading: true, preview: null, err: "", busy: false, sent: null });
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/feedback/projects/${projectId}/preview`);
                setState((s) => ({ ...s, loading: false, preview: data }));
            } catch (e) {
                setState((s) => ({ ...s, loading: false, err: (e?.response?.data?.detail) || e?.message || "Errore" }));
            }
        })();
    }, [projectId]);
    const send = async () => {
        setState((s) => ({ ...s, busy: true, err: "" }));
        try {
            const { data } = await api.post(`/feedback/projects/${projectId}/send`);
            setState((s) => ({ ...s, busy: false, sent: data }));
        } catch (e) {
            setState((s) => ({ ...s, busy: false, err: (e?.response?.data?.detail) || e?.message || "Errore invio" }));
        }
    };
    const p = state.preview;
    const canSend = p && p.recipient && p.form_url && !state.sent;
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-lg w-full max-w-lg" data-testid="feedback-modal">
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <div className="text-xs text-neutral-500">Progetto concluso</div>
                        <div className="font-display font-semibold">Invia email di feedback</div>
                    </div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    {state.loading && <div className="text-neutral-500">Caricamento…</div>}
                    {!state.loading && p && (
                        <>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-neutral-500">Destinatario</div>
                                <div className="col-span-2 text-neutral-900 break-all" data-testid="feedback-recipient">{p.recipient || <span className="text-red-600">Il cliente non ha email</span>}</div>
                                <div className="text-neutral-500">Progetto</div>
                                <div className="col-span-2 text-neutral-900">{p.project_name}</div>
                                <div className="text-neutral-500">Link questionario</div>
                                <div className="col-span-2 text-neutral-900 break-all">{p.form_url || <span className="text-red-600">Non configurato</span>}</div>
                            </div>
                            <div>
                                <div className="text-xs text-neutral-500 mb-1">Oggetto</div>
                                <div className="text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5">{p.subject}</div>
                            </div>
                            <div>
                                <div className="text-xs text-neutral-500 mb-1">Corpo (anteprima)</div>
                                <div className="text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5 whitespace-pre-line">{p.body_plain}</div>
                            </div>
                        </>
                    )}
                    {state.err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{state.err}</div>}
                    {state.sent && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">✓ Email inviata</div>}
                </div>
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">
                        {state.sent ? "Chiudi" : "Non inviare"}
                    </button>
                    {!state.sent && (
                        <button onClick={send} disabled={!canSend || state.busy} data-testid="feedback-send"
                            className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                            {state.busy ? "Invio…" : "Invia email"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function getSortValue(p, key, clientMap, statusMap) {
    switch (key) {
        case "title": return (p.title || "").toLowerCase();
        case "type": return p.project_type || "";
        case "client": {
            const c = clientMap[p.client_id];
            return c ? `${c.first_name} ${c.last_name}`.toLowerCase() : "";
        }
        case "status": {
            const s = statusMap[p.status_id];
            return s ? s.name.toLowerCase() : "";
        }
        case "deadline": return p.end_date || "";
        default: return "";
    }
}

function Accordion({ title, count, expanded, onToggle, testId, children, variant }) {
    const muted = variant === "muted";
    return (
        <div className={muted
            ? "bg-neutral-100/60 border border-dashed border-neutral-200 rounded-md overflow-hidden opacity-90"
            : "bg-white border border-neutral-200 rounded-md overflow-hidden"} data-testid={testId}>
            <button
                onClick={onToggle}
                data-testid={`${testId}-toggle`}
                className={`w-full flex items-center gap-2 px-4 py-2 ${muted ? "hover:bg-neutral-100" : "hover:bg-neutral-50 border-b border-neutral-100"}`}
            >
                {expanded ? <ChevronDown size={14} className={muted ? "text-neutral-400" : ""} /> : <ChevronRight size={14} className={muted ? "text-neutral-400" : ""} />}
                <span className={`font-display text-xs font-medium tracking-wide uppercase ${muted ? "text-neutral-500" : "text-neutral-800"}`}>
                    {title}{count != null ? ` · ${count}` : ""}
                </span>
            </button>
            {expanded && <div>{children}</div>}
        </div>
    );
}

function ProjectsTable({ projects, clientMap, statusMap, statuses, metricsByProject, onStatusChange, testId, muted }) {
    if (projects.length === 0) {
        return <div className={`bg-white border ${muted ? "" : "border-neutral-200 rounded-md"} p-6 text-sm text-neutral-400`} data-testid={`${testId}-empty`}>Nessun progetto in questa sezione.</div>;
    }
    return (
        <div className={`${muted ? "opacity-70" : "bg-white border border-neutral-200 rounded-md"} overflow-hidden`} data-testid={testId}>
            <table className="w-full text-sm">
                <thead className="text-xs text-neutral-500 border-b border-neutral-100">
                    <tr>
                        <th className="text-left px-4 py-2 font-medium">Titolo</th>
                        <th className="text-left px-4 py-2 font-medium">Tipo</th>
                        <th className="text-left px-4 py-2 font-medium">Cliente</th>
                        <th className="text-left px-4 py-2 font-medium">Stato</th>
                        <th className="text-left px-4 py-2 font-medium">Avanzamento</th>
                        <th className="text-left px-4 py-2 font-medium">Scadenza</th>
                    </tr>
                </thead>
                <tbody>
                    {projects.map((p) => {
                        const s = statusMap[p.status_id];
                        const c = clientMap[p.client_id];
                        const pct = metricsByProject?.[p.id]?.project_percentage ?? 0;
                        return (
                            <tr key={p.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`project-row-${p.id}`}>
                                <td className="px-4 py-2"><Link to={`/progetti/${p.id}`} className="font-medium text-neutral-900 hover:underline">{p.title}</Link></td>
                                <td className="px-4 py-2 text-xs"><span className={`px-1.5 py-0.5 rounded border ${p.project_type === "continuous" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-neutral-100 text-neutral-700 border-neutral-200"}`}>{p.project_type === "continuous" ? "Continuativo" : "Spot"}</span></td>
                                <td className="px-4 py-2 text-neutral-700">{c ? <Link to={`/clienti/${c.id}`} className="hover:underline">{c.first_name} {c.last_name}</Link> : "—"}</td>
                                <td className="px-4 py-2">
                                    <InlineStatusSelect
                                        value={p.status_id || ""}
                                        current={s}
                                        statuses={statuses}
                                        onChange={(sid) => onStatusChange(p.id, sid)}
                                        testId={`status-select-${p.id}`}
                                    />
                                </td>
                                <td className="px-4 py-2">
                                    <div className="flex items-center gap-2 min-w-[120px]" data-testid={`project-progress-${p.id}`}>
                                        <div className="w-24 h-1.5 rounded bg-neutral-100 overflow-hidden">
                                            <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="text-xs text-neutral-600 tabular-nums">{pct}%</span>
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-neutral-600 text-xs">{formatDateIt(p.end_date)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function InlineStatusSelect({ value, current, statuses, onChange, testId }) {
    return (
        <div className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: current?.color || "#cbd5e1" }} />
            <select
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="text-xs border border-neutral-200 rounded px-1.5 py-0.5 bg-white hover:border-neutral-400 focus:outline-none focus:border-neutral-500"
                style={{ color: current?.color || "#334155" }}
            >
                {statuses.map((s) => <option key={s.id} value={s.id} style={{ color: "#111" }}>{s.name}</option>)}
            </select>
        </div>
    );
}

function NewProjectModal({ clients, statuses, users, onClose, onCreated }) {
    const [localClients, setLocalClients] = useState(clients);
    const [form, setForm] = useState({
        title: "", description: "", project_type: "spot",
        client_id: clients[0]?.id || "", status_id: statuses[0]?.id || "",
        assignee_id: users[0]?.id || "",
        start_date: "", end_date: "",
    });
    const [showNewClient, setShowNewClient] = useState(false);
    const [categories, setCategories] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/sop-templates", { params: { type: "project" } }).then((r) => setTemplates(r.data)).catch(() => {});
        api.get("/settings/client-categories").then((r) => setCategories(r.data)).catch(() => {});
        api.get("/collaborators").then((r) => setCollaborators(r.data)).catch(() => {});
    }, []);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/projects", { ...form });
            if (templateId) {
                try { await api.post(`/sop-templates/${templateId}/apply/project/${data.id}`); }
                catch (e) { console.error("SOP apply failed:", e); }
            }
            onCreated(data);
        } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                className="bg-white rounded-md shadow-lg w-full max-w-lg" data-testid="new-project-modal">
                <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                    <div className="font-display font-semibold">Nuovo progetto</div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <F label="Titolo*"><input required data-testid="project-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></F>
                    <F label="Descrizione"><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></F>
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Tipo">
                            <select data-testid="project-type" value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                                <option value="spot">Spot</option>
                                <option value="continuous">Continuativo</option>
                            </select>
                        </F>
                        <F label="Cliente*">
                            <div className="flex items-center gap-1">
                                <div className="flex-1">
                                    <ClientAutocomplete clients={localClients}
                                        value={form.client_id}
                                        onChange={(id) => setForm({ ...form, client_id: id })}
                                        onClientCreated={(c) => { setLocalClients([c, ...localClients]); setForm({ ...form, client_id: c.id }); }}
                                        testId="project-client-autocomplete" />
                                </div>
                                <button type="button" onClick={() => setShowNewClient(true)}
                                    data-testid="quick-new-client-btn"
                                    title="Crea nuovo cliente al volo"
                                    className="p-1.5 border border-neutral-300 rounded hover:bg-neutral-50 text-neutral-700">
                                    <Plus size={14} />
                                </button>
                            </div>
                        </F>
                        <F label="Stato">
                            <select value={form.status_id} onChange={(e) => setForm({ ...form, status_id: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                                {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </F>
                        <F label="Responsabile">
                            <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                                {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                            </select>
                        </F>
                        <F label="Inizio"><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></F>
                        <F label="Fine"><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></F>
                    </div>
                    {templates.length > 0 && (
                        <F label="Applica template SOP (facoltativo)">
                            <select data-testid="project-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                                <option value="">— Nessun template —</option>
                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </F>
                    )}
                </div>
                {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button type="submit" disabled={busy} data-testid="save-project-btn" className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">{busy ? "Salvataggio…" : "Crea progetto"}</button>
                </div>
            </form>
            {showNewClient && (
                <NewClientModal categories={categories} collaborators={collaborators}
                    onClose={() => setShowNewClient(false)}
                    onCreated={(c) => { setLocalClients([c, ...localClients]); setForm({ ...form, client_id: c.id }); setShowNewClient(false); }} />
            )}
        </div>
    );
}

function F({ label, children }) { return <label className="block"><span className="text-xs font-medium text-neutral-600">{label}</span><div className="mt-1">{children}</div></label>; }
